import { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { branchTermSingularCapitalised } from 'utils/terminology';
import { HookFormStepContext } from 'components/cef_form/hook-form/providers/HookFormStepProvider';
import { HookFormContext } from 'components/cef_form/hook-form/providers/HookFormProvider';
import Button from 'components/ui-components/Button';
import Card from 'components/ui-components/Card';
import { gaRegisterFormSteps } from 'data_layer/dataLayerRegisterForm';

import useHookFormStepNavigation from 'components/cef_form/hook-form/hooks/useHookFormStepNavigation';
import steps from 'components/mycef/Register/RegisterForm/formSteps';
import ProgressBar from '../ProgressBar';

const StepTemplate = ({ step, validate, additionalContent, children }) => {
  const {
    stepTitle,
    hasNav,
    onSubmit: onSubmitFromContext,
    navigate,
    reachedStep,
  } = useContext(HookFormStepContext);

  const {
    watchedFields: { companyType },
  } = useContext(HookFormContext);

  const [governmentRoute, setGovernmentRoute] = useState(false);
  const findABranch = () => window.open('/stores', '_blank');

  useEffect(() => {
    gaRegisterFormSteps(stepTitle, 'Start'); // Tell GA the step is started
  }, []);

  useEffect(() => {
    if (step === 1 && companyType === '3') {
      setGovernmentRoute(true);
    } else {
      setGovernmentRoute(false);
    }
  }, [step, companyType]);

  const onSubmit = event => {
    event.preventDefault();

    // If additional validation required for this step
    if (validate) {
      const isValid = validate();

      if (isValid) {
        onSubmitFromContext(event);
      }
    } else {
      // Run the main submit function as normal
      onSubmitFromContext(event);
    }
  };

  // Progress bar data
  const stepComponents = Object.values(steps).map(s => s.component);
  const { setCurrentStep } = useHookFormStepNavigation();

  return (
    <form onSubmit={onSubmit}>
      {stepTitle && (
        <h2
          data-testid={stepTitle}
          className="ta-c ff-body fw-700 fs-30 s-fs-34 m-fs-36 lh-1 tc-main">
          {governmentRoute ? 'Government/Local Authority/Charity' : stepTitle}
        </h2>
      )}
      {governmentRoute && (
        <div className="fl jc-c">
          <p className="ta-c fs-18 tc-secondary mb-24 register-form__subheading">
            This type of account cannot be registered online. Please download
            the paper copy of our application and follow the instructions.
          </p>
        </div>
      )}

      <div className="fl jc-c">
        <Card
          variant="large"
          className="register-form__card m-fb-70 l-fb-35 pos-r ov-hidden">
          {step && !governmentRoute && (
            <ProgressBar
              stepComponents={stepComponents}
              currentStep={step}
              setCurrentStep={setCurrentStep}
              reachedStep={reachedStep}
              navigate={navigate}
              validate={validate}
            />
          )}
          <div>
            {step && !governmentRoute && (
              <>
                <div className="register-progress-divider" />
                <br />
              </>
            )}
            {children}
          </div>

          {hasNav && (
            <div className={governmentRoute ? 'mt-16' : 'mt-32'}>
              <Button
                className="cef-btn cef-btn--feature cef-btn--green width-100 m-width-auto cef-btn--xxl"
                type={governmentRoute ? 'button' : 'submit'}
                handleClick={() => governmentRoute && findABranch()}>
                {governmentRoute
                  ? `Find a ${branchTermSingularCapitalised}`
                  : 'Continue'}
              </Button>
            </div>
          )}

          {additionalContent && (
            <>
              <hr className="mtb-36" aria-hidden="true" /> {additionalContent}
            </>
          )}
        </Card>
      </div>
      {!governmentRoute && (
        <div className="mt-24 ta-c fs-14 tc-secondary">
          {'This site is protected by reCAPTCHA and the Google '}
          <a
            href="https://policies.google.com/privacy"
            target="_blank"
            rel="noreferrer"
            className="tc-secondary td-u">
            Privacy Policy
          </a>
          {' and '}
          <a
            href="https://policies.google.com/terms"
            target="_blank"
            rel="noreferrer"
            className="tc-secondary td-u">
            Terms of Service
          </a>
          {' apply.'}
        </div>
      )}
    </form>
  );
};

StepTemplate.propTypes = {
  step: PropTypes.number,
  validate: PropTypes.func,
  additionalContent: PropTypes.node,
  children: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
};

export default StepTemplate;
